import { debounce } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';

import { useGetCustomerKYXVerificationsQuery } from '@/api/v1';
import { Clickable } from '@/components/atoms/Clickable';
import { NoResultsView } from '@/components/atoms/NoResultsView';
import { TableCellErrorBoundary } from '@/components/atoms/TableCellErrorBoundary';
import { TableContentSelector } from '@/components/atoms/TableContentSelector';
import { TableRowErrorBoundary } from '@/components/atoms/TableRowErrorBoundary';
import { CustomerTableCell } from '@/components/molecules/CustomerTableCell';
import { HeaderCell } from '@/components/molecules/HeaderCell';
import { SearchBox } from '@/components/molecules/SearchBox';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { MainScreen } from '@/components/organisms/MainScreen';
import { useTranslator } from '@/hooks/useTranslator';
import { useTypedSearchParams } from '@/hooks/useTypedSearchParams';
import { CustomersFilters } from '@/Screens/Dashboard/Customers/CustomersFilters';
import { createHeaders } from '@/Screens/Dashboard/Customers/headers';
import { NoCustomersView } from '@/Screens/Dashboard/Customers/NoCustomersView';
import { CustomerRow } from '@/Screens/Dashboard/Customers/types';
import { GetCustomerKYXVerificationsFilters } from '@/types/getCustomerKYXVerificationsFilters';
import { HeaderItem } from '@/types/headerItem';
import { Page } from '@/types/page';
import { computeSortingValue, parseSort } from '@/utility/sorting';

export const Customers: React.FC = (): React.ReactElement => {
  const tr = useTranslator();
  const navigate = useNavigate();

  const [filters, setFilters] = useTypedSearchParams<GetCustomerKYXVerificationsFilters>(
    GetCustomerKYXVerificationsFilters.initial(),
  );
  const [filtersModalOpen, setFiltersModalOpen] = React.useState<boolean>(false);

  const { data = Page.empty(), status } = useGetCustomerKYXVerificationsQuery(filters);

  const {
    content: customers,
    totalPages,
    totalElements,
    pageable: { pageNumber },
  } = data;
  const headers = React.useMemo((): readonly HeaderItem<CustomerRow>[] => createHeaders(tr), [tr]);
  const sorting = React.useMemo((): Record<string, 'asc' | 'desc'> => {
    const sort = filters.sort;

    if (typeof sort === 'string') {
      return parseSort([sort]);
    } else if (sort.length > 0) {
      return parseSort(sort);
    }

    return { createdDate: 'desc' };
  }, [filters.sort]);

  const filtersCount = React.useMemo((): number => countFilters(filters), [filters]);

  const handleSort = React.useCallback(
    (column: string): void => {
      setFilters(
        (filters: GetCustomerKYXVerificationsFilters): GetCustomerKYXVerificationsFilters => {
          const sort = typeof filters.sort === 'string' ? [filters.sort] : filters.sort;

          const current = sort
            .map((item: string): [string, string] => {
              const [targetColumn, direction] = item.split(',');

              return [targetColumn, direction];
            })
            .filter(
              (item: [string, string]): item is [string, 'asc' | 'desc'] =>
                item[1] === 'asc' || item[1] === 'desc',
            )
            .find(([targetColumn]: [string, 'asc' | 'desc']): boolean => {
              return targetColumn === column;
            });

          if (!current) {
            return {
              ...filters,
              sort: [`${column},asc`, ...sort],
              page: 1,
            };
          }

          const [, direction] = current;
          const computedDirection = computeSortingValue(direction);

          return {
            ...filters,
            sort: [
              `${column},${computedDirection}`,
              ...sort.filter((item: string): boolean => item.split(',')?.[0] !== column),
            ],
            page: 1,
          };
        },
      );
    },
    [setFilters],
  );

  const handlePageChange = React.useCallback(
    (_: React.ChangeEvent<unknown>, page: number): void => {
      setFilters(
        (filters: GetCustomerKYXVerificationsFilters): GetCustomerKYXVerificationsFilters => ({
          ...filters,
          page: page,
        }),
      );
    },
    [setFilters],
  );

  const onRowClick = React.useCallback(
    (customerId: string): void => {
      navigate(customerId);
    },
    [navigate],
  );

  const onFiltersModalClose = React.useCallback((): void => {
    setFiltersModalOpen(false);
  }, []);

  const openFiltersModal = React.useCallback((): void => {
    setFiltersModalOpen(true);
  }, []);

  const handleFiltersChange = React.useCallback(
    (filters: GetCustomerKYXVerificationsFilters): void => {
      setFilters({ ...filters, page: 1 });
      setFiltersModalOpen(false);
    },
    [setFilters],
  );

  const handleSearch = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value: search } = event.target;

      setFilters(
        (filters: GetCustomerKYXVerificationsFilters): GetCustomerKYXVerificationsFilters => ({
          ...filters,
          name: search.trim(),
          page: 1,
        }),
      );
    },
    [setFilters],
  );

  const debouncedHandleSearch = React.useMemo(
    (): ReturnType<typeof debounce> => debounce(handleSearch, 300),
    [handleSearch],
  );

  React.useEffect((): VoidFunction => {
    return (): void => {
      debouncedHandleSearch.clear();
    };
  }, [debouncedHandleSearch]);

  return (
    <MainScreen title={tr('CUSTOMERS')}>
      <Box display="flex" flexDirection="column" gap={4} height="100%">
        <Typography variant="body1" color="text.secondary" mt={-3}>
          {tr('CUSTOMERS_SUBTITLE')}
        </Typography>

        <Stack direction="row" gap={2} justifyContent="flex-end">
          <SearchBox placeholder={tr('SEARCH_BY_NAME')} onChange={debouncedHandleSearch} />
          <Button variant="light" size="compact" onClick={openFiltersModal}>
            {filtersCount === 0 ? tr('FILTER') : tr('FILTER_WITH_COUNT', filtersCount)}
          </Button>
          <Button component={Link} to="new" variant="contained" size="compact">
            {tr('ADD_NEW_CUSTOMER')}
          </Button>
        </Stack>
        <Paper elevation={3} sx={styles.paper}>
          <TableContentSelector
            status={status}
            data={customers}
            filtersCount={filtersCount}
            noContentView={<NoCustomersView />}
            noResultsView={
              <NoResultsView
                title={tr('ZERO_RESULTS_FOUND')}
                message={tr(
                  'TRY_CHANGING_YOUR_APPLIED_FILTERS',
                  tr('CUSTOMERS').toLocaleLowerCase(),
                )}
              />
            }>
            <Table sx={styles.table}>
              <colgroup>
                {widths.map(
                  (width: number, index: number): React.ReactElement => (
                    <col key={index} width={width / totalWidth} />
                  ),
                )}
              </colgroup>
              <TableHead>
                <TableRow>
                  {headers.map(
                    (item: HeaderItem<CustomerRow>): React.ReactElement => (
                      <HeaderCell
                        key={item.key}
                        item={item}
                        sorting={sorting}
                        onClick={handleSort}
                      />
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.map(
                  (row: CustomerRow): React.ReactElement => (
                    <TableRowErrorBoundary
                      key={row.id}
                      columnCount={headers.length}
                      fallbackMessage="Unable to display customer data">
                      <Clickable data={row.id} onClick={onRowClick}>
                        <TableRow key={row.sessionId} sx={styles.row} hover>
                          {headers.map(
                            (header: HeaderItem<CustomerRow>): React.ReactElement => (
                              <TableCellErrorBoundary
                                key={header.key}
                                sx={styles.cell}
                                fallbackMessage="N/A">
                                <CustomerTableCell
                                  key={header.key}
                                  customer={row}
                                  field={header.key}
                                  sx={styles.cell}
                                />
                              </TableCellErrorBoundary>
                            ),
                          )}
                        </TableRow>
                      </Clickable>
                    </TableRowErrorBoundary>
                  ),
                )}
              </TableBody>
            </Table>
          </TableContentSelector>
        </Paper>
        {totalElements > 0 && (
          <Box display="flex" alignItems="center" justifyContent="center">
            <Pagination count={totalPages} page={pageNumber + 1} onChange={handlePageChange} />
          </Box>
        )}
      </Box>

      {filtersModalOpen && (
        <ApplicationDialog onClose={onFiltersModalClose}>
          <ApplicationModal
            title={tr('FILTER')}
            description={tr('SELECT_FILTERS_THAT_APPLY')}
            onClose={onFiltersModalClose}>
            <CustomersFilters filters={filters} onChange={handleFiltersChange} />
          </ApplicationModal>
        </ApplicationDialog>
      )}

      <Outlet />
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  paper: {
    flex: 1,
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
  },
  cell: {
    minWidth: 0,
    py: 2,
    '&:first-of-type': {
      pl: 5,
    },
    '&:last-of-type': {
      pr: 5,
    },
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  row: {
    cursor: 'pointer',
  },
};

const COLUMN_WIDTHS = {
  TYPE: 100,
  NAME: 200,
  STATUS: 150,
  RESULT: 150,
  CREATED_AT: 200,
} as const;

const widths = Object.values(COLUMN_WIDTHS);
const totalWidth = widths.reduce((total: number, width: number): number => total + width, 0);

const countFilters = (filters: GetCustomerKYXVerificationsFilters): number => {
  return Object.entries(filters).reduce(
    (totalCount: number, [key, value]: [string, unknown]): number => {
      switch (key) {
        case 'type':
        case 'fromDate':
        case 'toDate':
        case 'verificationStatus':
        case 'verificationResult':
          return totalCount + (value !== undefined ? 1 : 0);
        default:
          return totalCount;
      }
    },
    0,
  );
};
